import { PageLayout } from "@/components/page-layout/PageLayout";
import { CustomerInfoPairBox } from "@/features/product-and-price/info-boxes/customer/CustomerInfoPairBox";
import {
  EditHeadAgreementCustomerQuery,
  EditHeadAgreementCustomerQueryVariables,
} from "@/gql/graphql";
import { gql, useQuery } from "@apollo/client";
import { BreadCrumbs } from "@stenajs-webui/elements";
import { ErrorScreen } from "@stenajs-webui/panels";
import * as React from "react";
import { Crumb } from "@/components/bread-crumbs/Crumb";
import { PageHeader } from "@/components/page-header/PageHeader";
import { routeFactory } from "../../../../../RouteFactory";
import { HeadAgreementModelLoader } from "./HeadAgreementModelLoader";

interface Props {
  customerId: string;
  headAgreementId: string;
}

const query = gql`
  query EditHeadAgreementCustomer($customerId: ID!, $headAgreementId: ID!) {
    productPrice {
      customers {
        byId(id: $customerId) {
          id
          name
          breadCrumbName
          custIndex
          custNo
          isParent
          custStatus {
            id
            name
          }
          parent {
            id
            preselectedHeadAgreement {
              id
            }
          }
          childCustomers {
            id
          }
          statisticalGrouping {
            id
          }
          invoiceCurrency {
            id
            code
          }
          seller {
            id
            name
          }
          salesRegion {
            id
            code
            name
          }
          isStandardCustomer
          hasRebateAgreements
          writeHistory {
            createdAt {
              isoString
            }
            createdBy {
              id
              name
            }
            modifiedAt {
              isoString
            }
            modifiedBy {
              id
              name
            }
          }
        }
      }
      headAgreement {
        byId(headAgreementId: $headAgreementId) {
          id
          headAgreementNumber
          breadCrumbName
        }
      }
    }
  }
`;

export const EditHeadAgreement: React.FC<Props> = ({
  customerId,
  headAgreementId,
}) => {
  const { data, loading, error } = useQuery<
    EditHeadAgreementCustomerQuery,
    EditHeadAgreementCustomerQueryVariables
  >(query, {
    variables: {
      customerId,
      headAgreementId,
    },
  });

  const customer = data?.productPrice.customers.byId;

  return (
    <>
      <PageHeader
        title={"Edit header agreement"}
        breadcrumbs={
          <BreadCrumbs>
            <Crumb
              label={customer?.breadCrumbName ?? ""}
              path={routeFactory.productAndPrice.customer.customer({
                customerId,
              })}
            />
            <Crumb
              label={String(
                data?.productPrice.headAgreement.byId?.headAgreementNumber ?? ""
              )}
              path={routeFactory.productAndPrice.customer.customerHeadAgreement(
                {
                  customerId,
                  headAgreementId:
                    data?.productPrice.headAgreement.byId?.id ?? "",
                }
              )}
            />
            <Crumb
              label={"edit"}
              path={routeFactory.productAndPrice.headAgreement.editHeadAgreement(
                {
                  customerId,
                  headAgreementId,
                }
              )}
            />
          </BreadCrumbs>
        }
      />

      {error && !loading && <ErrorScreen text={"No such customer."} />}
      {customer && (
        <PageLayout>
          <CustomerInfoPairBox customer={customer} />
          <HeadAgreementModelLoader
            customerId={customerId}
            headAgreementId={headAgreementId}
          />
        </PageLayout>
      )}
    </>
  );
};
